import { SETUP_CHAT_PURPOSE } from '@zerohuman/shared'
import { isPublicScreen } from '@/lib/publicScreens'

/** Where a new user lands once, straight after sign-up, to connect an AI account. */
export const ACCOUNT_STEP_PATH = '/onboarding/ai-account'

/** Where a plan is bought: the one screen the new user's landing never moves them off. */
export const BILLING_PATH = '/billing'

/** Where a person goes once their account is connected when setup is not open: the chat, as anywhere else. */
export const AFTER_ACCOUNT_STEP_PATH = '/chat'

/**
 * Where a person goes once their account is connected while setup is open (#1742, O1): the setup conversation with
 * the COO, whose first message is the next onboarding step. ChatView opens it and moves to its own address.
 */
export const SETUP_CHAT_PATH = `/chat?purpose=${SETUP_CHAT_PURPOSE}`

export interface OnboardingGateState {
    /**
     * The new user's one-time landing on the account step is due (#1796): an owner with no account who has never
     * reached the step. It is spent the first time they move off the step.
     */
    inOnboarding: boolean
    /** The enterprise has a usable AI account. */
    accountConnected: boolean
}

/**
 * What the gate knows. Until App.vue records it nothing is known and nothing is held, so a page load's first
 * navigation is not bounced before `me` and the account status answer.
 */
let gate: OnboardingGateState = { inOnboarding: false, accountConnected: true }

/** Records what the gate needs to know; the router's guard reads it. */
export function setOnboardingGate(state: OnboardingGateState): void {
    gate = { ...state }
}

/** What the gate last recorded. */
export function onboardingGate(): OnboardingGateState {
    return { ...gate }
}

/**
 * Where a navigation to `path` has to go instead, or null to let it through.
 *
 * #1796: no page is held because there is no AI account. Every page opens and shows what it has, and the actions that
 * would start model work are what ask for an account (`requireAiAccount`). The one thing left here is the new user's
 * landing: an owner who has never reached the account step, with no account, is sent there wherever they first
 * asked to go. The step itself always opens, and so does a screen that needs no sign-in (#1778): a decide link is
 * followed to the decision it was sent for. So does Billing: somebody who signed up for a paid plan arrives there to
 * pay for it, and is not taken off a checkout half done. The landing is still due at their next move.
 */
export function onboardingRedirect(path: string): string | null {
    if (!gate.inOnboarding || gate.accountConnected || isPublicScreen(path) || path === BILLING_PATH) {
        return null
    }
    return path === ACCOUNT_STEP_PATH ? null : ACCOUNT_STEP_PATH
}

/**
 * The person is moving off the account step. If the landing is what put them there (no account yet), it is spent:
 * they have reached the step, and from here on they go where they ask and are never sent back to it.
 */
export function leaveAccountStep(): void {
    if (gate.inOnboarding && !gate.accountConnected) {
        gate = { ...gate, inOnboarding: false }
    }
}
